import axios from "axios";
import {axiosCatchHandler} from "../sharedGlobal";
import SweetToast from "@/SweetToast";

const state = {
  competitorId: undefined,
  competitors: [],
  suggestedCompetitors: [],
  competitor: undefined,
  competitorAuditRefreshing: false,
  loadingCompetitor: false,
  // Keywords
  rankingKeywordsRan: true,
  keywords: [],
  keywordMeta: undefined,
  keywordSearch: '',
  keywordOrderBy: 'key',
  keywordOrderDirection: 'asc',
  // Backlinks
  backlinksRan: true,
  backlinks: [],
  domainAuthority: undefined,
  backlinksSearch: '',
  backlinksView: 'simple',
  backlinksOrderBy: 'domain',
  backlinksOrderDirection: 'asc',
  // Historic
  history: [],
  comparisonType: 'keyword',
  historicPerPage: 4,
  historicPage: 1,
  // Compare
  compareCompetitionId: undefined,
  websiteCompareData: undefined,
  compareCompetitors: [
    undefined,
    undefined,
    undefined,
    undefined,
  ],
  compareCompetitorsData: [
    undefined,
    undefined,
    undefined,
    undefined,
  ],
};

const getters = {
  competitorId: ({competitorId}) => competitorId,

  competitors: ({competitors}) => competitors,

  suggestedCompetitors: ({suggestedCompetitors}) => suggestedCompetitors,

  competitor: ({competitor}) => competitor,

  competitorAuditRefreshing: ({competitorAuditRefreshing}) => competitorAuditRefreshing,

  loadingCompetitor: ({loadingCompetitor}) => loadingCompetitor,

  rankingKeywordsRan: ({rankingKeywordsRan}) => rankingKeywordsRan,

  keywords: ({keywords}) => keywords,

  keywordMeta: ({keywordMeta}) => keywordMeta,

  keywordActualMetric: ({keywordMeta}) => keywordMeta?.actual_metric,

  keywordSearch: ({keywordSearch}) => keywordSearch,

  keywordOrderBy: ({keywordOrderBy}) => keywordOrderBy,

  keywordOrderDirection: ({keywordOrderDirection}) => keywordOrderDirection,

  backlinksRan: ({backlinksRan}) => backlinksRan,

  backlinks: ({backlinks}) => backlinks,

  domainAuthority: ({domainAuthority}) => domainAuthority,

  backlinksSearch: ({backlinksSearch}) => backlinksSearch,

  backlinksView: ({backlinksView}) => backlinksView,

  backlinksOrderBy: ({backlinksOrderBy}) => backlinksOrderBy,

  backlinksOrderDirection: ({backlinksOrderDirection}) => backlinksOrderDirection,

  history: ({history}) => history,

  comparisonType: ({comparisonType}) => comparisonType,

  historicPerPage: ({historicPerPage}) => historicPerPage,

  historicPage: ({historicPage}) => historicPage,

  compareCompetitionId: ({compareCompetitionId}) => compareCompetitionId,

  websiteCompareData: ({websiteCompareData}) => websiteCompareData,

  compareCompetitors: ({compareCompetitors}) => compareCompetitors,

  compareCompetitorsData: ({compareCompetitorsData}) => compareCompetitorsData,

  filteredRankedKeywords(state) {
    const search = state.keywordSearch;

    return state.keywords
      .filter(keyword => keyword.key.toLowerCase().includes(search.toLowerCase()))
      .sort(function (a, b) {
        const orderBy = state.keywordOrderBy;
        if (state.keywordOrderDirection === 'asc') {
          return a[orderBy] > b[orderBy] ? 1 : -1;
        }

        return a[orderBy] < b[orderBy] ? 1 : -1;
      });
  },

  filteredBacklinks(state) {
    const search = state.backlinksSearch;

    return state.backlinks
      .filter(backlink => backlink.url.toLowerCase().includes(search.toLowerCase()))
      .sort(function (a, b) {
        const orderBy = state.backlinksOrderBy;
        if (state.backlinksOrderDirection === 'asc') {
          return a[orderBy] > b[orderBy] ? 1 : -1;
        }

        return a[orderBy] < b[orderBy] ? 1 : -1;
      });
  },

  historyPages(state) {
    return Math.ceil(state.history.length / state.historicPerPage);
  },

  filteredHistory(state) {
    const startIndex = (state.historicPage - 1) * state.historicPerPage;
    const endIndex = startIndex + state.historicPerPage;

    return state.history.slice(startIndex, endIndex);
  },

  compareCompetitorColumns: (state) => state.compareCompetitors.map((data, index) => index + 1),

  isPendingResults: (state) => !state.rankingKeywordsRan || !state.backlinksRan,
};

const mutations = {
  setCompetitors(state, data) {
    state.competitors = data;

    if (state.competitor !== undefined) {
      return;
    }

    if (state.competitorId !== undefined) {
      const foundCompetitor = state.competitors.find(competitor => competitor.id === state.competitorId);
      if (foundCompetitor !== undefined) {
        state.competitor = foundCompetitor;
        return;
      }
    }

    state.competitor = data[0] ?? undefined;
  },

  setSuggestedCompetitors: (state, data) => state.suggestedCompetitors = data,

  setCompetitor: (state, competitor) => state.competitor = competitor,

  setCompetitorId: (state, id) => state.competitorId = isNaN(id) ? undefined : parseInt(id),

  setCompetitorLoading: (state, loading) => state.loadingCompetitor = loading,

  setCompetitorDetails(state, {data, isRefresh} = {}) {

    state.rankingKeywordsRan = data?.rankingKeywordsRan;
    state.keywordMeta = data?.rankedKeywords?.meta;
    state.keywords = data?.rankedKeywords?.ranked ?? [];
    state.backlinksRan = data?.backlinksRan;
    state.backlinks = data?.backlinks ?? [];
    state.domainAuthority = data?.domainAuthority;
    state.history = data?.history ?? [];
    if (!isRefresh) {
      state.historicPage = 1;
    }
  },

  setCompetitorAuditRefreshing: (state, refreshing) => state.competitorAuditRefreshing = refreshing,

  setKeywordSearch: (state, search) => state.keywordSearch = search,

  setKeywordOrder(state, orderBy) {
    if (orderBy === state.keywordOrderBy) {
      state.keywordOrderDirection = state.keywordOrderDirection === 'asc' ? 'desc' : 'asc';
      return;
    }
    state.keywordOrderBy = orderBy;
    state.keywordOrderDirection = 'asc';
  },

  setBacklinksSearch: (state, search) => state.backlinksSearch = search,

  setBacklinksView: (state, view) => state.backlinksView = view,

  setBacklinksOrder(state, orderBy) {
    if (orderBy === state.backlinksOrderBy) {
      state.backlinksOrderDirection = state.backlinksOrderDirection === 'asc' ? 'desc' : 'asc';
      return;
    }
    state.backlinksOrderBy = orderBy;
    state.backlinksOrderDirection = 'asc';
  },

  setComparisonType: (state, view) => state.comparisonType = view,

  setHistoricPage: (state, page) => state.historicPage = page,

  setWebsiteCompareData: (state, data) => state.websiteCompareData = data,

  setCompareCompetitionId: (state, id) => state.compareCompetitionId = id,

  setCompareCompetitor: (state, {column, competitor}) => state.compareCompetitors[column - 1] = competitor,

  setCompareCompetitorData: (state, {column, data}) => state.compareCompetitorsData[column - 1] = data,

  resetCompareCompetitors(state) {
    state.compareCompetitors = [
      undefined,
      undefined,
      undefined,
      undefined,
    ];

    state.compareCompetitorsData = [
      undefined,
      undefined,
      undefined,
      undefined,
    ];
  }
};

const actions = {
  fetchCompetitors: ({commit}) => {
    return new Promise((resolve, reject) => {
      axios.get('/ajax/v2/competitors')
        .then(({data}) => {
          commit('setCompetitors', data.data)
          resolve(data.data);
        }).catch((e) => {
          reject(e);
      });
    });
  },

  fetchSuggestedCompetitors: ({commit}) =>
    axios.get(`/ajax/v2/competitors/suggestions`)
      .then(({data}) => commit('setSuggestedCompetitors', data.data)),

  async addCompetitor({dispatch, commit}, competitorUrl) {
    return new Promise(async (resolve, reject) => {
      axios.post('/ajax/competition/add-competitor', {
        competitor_url: competitorUrl
      })
        .then((response) => {
          resolve(response.data);
          dispatch('fetchCompetitors').then((competitors) => {
            const newCompetitor = competitors.find((competitor) => competitor.id === response.data.id);
            if (!newCompetitor) return;

            commit('setCompetitor', newCompetitor);
          });
          dispatch('fetchSuggestedCompetitors');
          SweetToast.fire({
            icon: 'success',
            title: 'Competitor added successfully',
          });
        })
        .catch(
          (e) => {
            axiosCatchHandler(e, ({response}) => reject(response));
            SweetToast.fire({
              icon: 'error',
              title: e.response.data.message,
            });
          }
        );
    });
  },

  async refreshCompetitorAudit({commit, dispatch}, competitorId) {
    commit('setCompetitorAuditRefreshing', true);
    return new Promise((resolve, reject) => {
      axios.post(`/ajax/v2/competitors/${competitorId}/refresh-audit`)
        .then((response) => {
          commit('setCompetitorAuditRefreshing', false);
          dispatch('fetchCompetitors');
          dispatch('fetchCompetitorDetails');
          SweetToast.fire({
            icon: 'success',
            title: 'Competitor refreshing...',
          });
          resolve(response);
        }).catch((e) => {
        commit('setCompetitorAuditRefreshing', false);
        axiosCatchHandler(e, ({response}) => reject(response));
        SweetToast.fire({
          icon: 'error',
          title: e.response.data.message ?? 'Failed to refresh competitor',
        });
        reject(e);
      });
    });
  },

  async removeCompetitor({dispatch}, competitorId) {
    return new Promise(async (resolve, reject) => {
      axios.delete(`/ajax/v2/competitors/${competitorId}`)
        .then(() => {
          resolve();
          dispatch('fetchCompetitors');
          dispatch('fetchSuggestedCompetitors');
          SweetToast.fire({
            icon: 'success',
            title: 'Competitor removed successfully',
          });
        })
        .catch(
          (e) => {
            axiosCatchHandler(e, ({response}) => reject(response));
            SweetToast.fire({
              icon: 'error',
              title: e.response.data.message,
            });
          }
        );
    });
  },

  fetchCompetitorDetails({commit, state}, isRefresh = false) {
    if (!state.competitor) {
      return;
    }
    if (!isRefresh) {
      commit('setCompetitorLoading', true);
    }
    axios.get(`/ajax/v2/competitors/${state.competitor.id}`).then(({data}) => {
      // Don't update data if keywords or backlinks haven't run, this improves performance
      if (
        isRefresh &&
        state.rankingKeywordsRan === data.data.rankingKeywordsRan &&
        state.backlinksRan === data.data.backlinksRan
      ) {
        commit('setCompetitorLoading', false);
        return;
      }
      // Something has changed so update the competitor data
      commit('setCompetitorDetails', {
        data: data.data,
        isRefresh,
      });
      commit('setCompetitorLoading', false);
    }).catch((error) => {
      SweetToast.fire({
        icon: 'error',
        title: 'Something went wrong whilst trying to load competitor',
      });
      commit('setCompetitorDetails', undefined);
      commit('setCompetitorLoading', false);
      console.error(error);
    });
  },

  fetchWebsiteCompareData: ({commit}) =>
    axios.get(`/ajax/v2/website/compare`)
      .then(({data}) => commit('setWebsiteCompareData', data.data)),

  fetchCompetitorCompareData({commit, state}, column) {
    const competitor = state.compareCompetitors[column - 1] ?? undefined;

    if (competitor === undefined) {
      commit('setCompareCompetitorData', {
        column,
        data: undefined
      });
      return;
    }

    axios.get(`/ajax/v2/competitors/${competitor.id}/compare`)
      .then(({data}) => commit('setCompareCompetitorData', {
        column,
        data: data.data
      }));
  },

  setBacklinksSearch: ({commit}, search) => commit('setBacklinksSearch', search),

  setBacklinksView: ({commit}, view) => commit('setBacklinksView', view),

  setBacklinksOrder: ({commit}, name) => commit('setBacklinksOrder', name),

  resetCompareCompetitors: ({commit}) => commit('resetCompareCompetitors'),

  setCompareCompetitor: ({commit}, payload) => commit('setCompareCompetitor', payload),

  setComparisonType: ({commit}, type) => commit('setComparisonType', type),

  setCompetitorId: ({commit}, id) => commit('setCompetitorId', id),

  setCompetitorDetails: ({commit}, details) => commit('setCompetitorDetails', {data: details}),

  setCompetitor: ({commit}, competitor) => commit('setCompetitor', competitor),

  setKeywordSearch: ({commit}, search) => commit('setKeywordSearch', search),

  setKeywordOrder: ({commit}, order) => commit('setKeywordOrder', order),

  setHistoricPage: ({commit}, page) => commit('setHistoricPage', page),

};

const competitionV2 = {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};

export default competitionV2;
